// Audio playback queue per il TTS streaming.
//
// Riceve chunks mp3 base64 dall'SSE, li decodifica con AudioContext, li
// accoda e li riproduce in sequenza. Una sola istanza per chat, lazy init
// al primo uso (AudioContext richiede user gesture su browser modern).

const BASE64_BUFFER_MIN_SIZE = 8192; // ~256ms di mp3 a 128kbps; sotto, accumuliamo

export class VoicePlayback {
  private context: AudioContext | null = null;
  private queue: AudioBuffer[] = [];
  private playing = false;
  private pendingChunks: Uint8Array[] = []; // accumula chunks fino a una soglia
  private currentSource: AudioBufferSourceNode | null = null;
  private onEndedCallback: (() => void) | null = null;
  private onErrorCallback: ((err: Error) => void) | null = null;

  /** Lazy init AudioContext: deve essere chiamato durante un user gesture (e.g. click MicButton). */
  ensureContext(): AudioContext {
    if (!this.context) {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.context = new Ctx();
    }
    return this.context;
  }

  setOnEnded(cb: (() => void) | null): void {
    this.onEndedCallback = cb;
  }

  setOnError(cb: ((err: Error) => void) | null): void {
    this.onErrorCallback = cb;
  }

  /**
   * Accoda un chunk base64. Quando l'accumulo arriva alla soglia, decode e play.
   * Nota: i chunks ElevenLabs sono mp3 raw senza container — non sono decodificabili
   * isolati. Accumuliamo finché non abbiamo abbastanza per un decode.
   */
  async pushChunk(base64: string): Promise<void> {
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    this.pendingChunks.push(bytes);
  }

  /**
   * Chiamato quando il server invia 'audio_end'. Concatena tutti i pending,
   * decoda e fa play. È l'unica strategia robusta con mp3 streaming senza
   * sapere a priori i frame boundaries — qualità latenza inferiore ma
   * pratica e affidabile.
   */
  async finalize(): Promise<void> {
    if (this.pendingChunks.length === 0) return;
    const totalLen = this.pendingChunks.reduce((s, c) => s + c.byteLength, 0);
    if (totalLen < BASE64_BUFFER_MIN_SIZE) {
      this.pendingChunks = [];
      return;
    }
    const merged = new Uint8Array(totalLen);
    let off = 0;
    for (const c of this.pendingChunks) {
      merged.set(c, off);
      off += c.byteLength;
    }
    this.pendingChunks = [];

    const ctx = this.ensureContext();
    let buffer: AudioBuffer;
    try {
      // decodeAudioData richiede ArrayBuffer (transfer ownership)
      const ab = merged.buffer.slice(merged.byteOffset, merged.byteOffset + merged.byteLength);
      buffer = await ctx.decodeAudioData(ab as ArrayBuffer);
    } catch (err) {
      this.onErrorCallback?.(err as Error);
      return;
    }
    this.queue.push(buffer);
    if (!this.playing) {
      this.playNext();
    }
  }

  private playNext(): void {
    const ctx = this.context;
    if (!ctx) {
      this.playing = false;
      this.onEndedCallback?.();
      return;
    }
    const buf = this.queue.shift();
    if (!buf) {
      this.playing = false;
      this.onEndedCallback?.();
      return;
    }
    this.playing = true;
    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.connect(ctx.destination);
    source.onended = () => {
      this.currentSource = null;
      this.playNext();
    };
    this.currentSource = source;
    source.start();
  }

  /** Stop immediato e svuota queue. */
  stop(): void {
    if (this.currentSource) {
      try {
        this.currentSource.stop();
      } catch {
        // already stopped
      }
      this.currentSource = null;
    }
    this.queue = [];
    this.pendingChunks = [];
    this.playing = false;
  }

  isPlaying(): boolean {
    return this.playing;
  }
}
